import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef, ViewChild, OnDestroy, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { MonitoringService, UserService } from '@myapp/modules/fwcore/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DrawerItem } from '@progress/kendo-angular-layout';
import { OktaWrapperService } from '../services/framework';
import { FooterComponent } from './modules/fwcore/components/footer/footer.component';
import { MenuVerticalComponent } from './modules/fwcore/components/menu-vertical/menu-vertical.component';
import { ToastContainerComponent } from './modules/fwcore/components/toast-container/toast-container.component';
import { TopbarComponent } from './modules/fwcore/components/topbar/topbar.component';

// Script for adding Clarity
const clarityScript = (clarityKey: string) => '(function (c, l, a, r, i, t, y) { c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }; t = l.createElement(r);'
  + ' t.async = 1; t.src = "https://www.clarity.ms/tag/" + i; y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);'
  + '})(window, document, "clarity", "script", "' + clarityKey + '");';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('panelInOut', [
      transition('void => *', [
        style({ transform: 'translateX(-100%)' }),
        animate(400)
      ]),
      transition('* => void', [
        animate(400, style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ],
  standalone: true,
  imports: [CommonModule, DialogModule, RouterOutlet, TopbarComponent, MenuVerticalComponent, FooterComponent, ToastContainerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  // Renamed output emitter to avoid name collision with toggleMenu() method
  @Output() toggleMenuEmitter: EventEmitter<any> = new EventEmitter<any>();

  showTopbar: boolean = true;
  showMenubar: boolean = true;
  showFooter: boolean = true;
  isLoading: boolean = false;
  menuHidden: boolean = false;
  jsonData: any[] = [];
  currentRoles: any[] = [];
  pageStart: number = 0;
  public items: Array<DrawerItem> = [];

  // Use a plain string for impersonationEmail
  protected impersonationEmail: string = '';

  // Use Angular EventEmitter for impersonateChanged
  @Output() impersonateChanged: EventEmitter<string> = new EventEmitter<string>();

  // Use Angular @ViewChild to get the MenuVerticalComponent instance from the template variable 'lhsMenu'
  @ViewChild('lhsMenu') protected compMenuVertical?: MenuVerticalComponent;

  // Set defaults for idle timeout
  idleLength: number = 30;
  idleCountdown: number = 30;
  idleTimeout: number = 30;

  @ViewChild('idleContent') idleContent?: TemplateRef<any>;

  constructor(private router: Router
    , private monitoring: MonitoringService
    , @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
    , @Inject('CONFIG_SETTINGS') private configSettings: any
    , private _idle: Idle, private wrapper: OktaWrapperService
    , protected userService: UserService
    , private modalService: NgbModal
    , private cdr: ChangeDetectorRef) {

    const idleSettings = configSettings[<any>'idle'];

    this.idleLength = idleSettings[<any>'length'];
    this.idleCountdown = idleSettings[<any>'countdown'];
    this.idleTimeout = idleSettings[<any>'timeout'];


    // Subscribe to page events for monitoring
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        monitoring.startNavigationEvent(event.url);
        this.pageStart = +new Date();
      } else if (event instanceof NavigationEnd) {

        monitoring.endNavigationEvent(event.url);

        this.oktaAuth.getUser().then(userClaim => {
          var user: string | undefined = '';

          if (userClaim) {
            user = userClaim.preferred_username ?? '';
          }

          const token = this.oktaAuth.getAccessToken();
          if (token) { monitoring.logPageView(event.url, user, token); }
        });
      }
    });

    // Handle page events for showing/hiding components
    // Removing Sidebar, Navbar, Footer for Documentation, Error and Auth pages
    router.events.forEach((event) => {
      if (event instanceof RoutesRecognized) { // was NavigationStart - changed to intercept redirect
        if ((event['url'] == '/login/callback') || (event['urlAfterRedirects'] == '/login') || (event['url'] == '/login')
          || (event['urlAfterRedirects'] == '/error/404') || (event['url'] == '/error/404')
          || (event['urlAfterRedirects'] == '/error/500') || (event['urlAfterRedirects'] == '/error/500')
          || (event['urlAfterRedirects'] == '/error/not-authorized') || (event['url'] == '/error/not-authorized')
        ) {
          this.showTopbar = false;
          this.showMenubar = false;
          this.showFooter = false;

          document.querySelector('.isg-page-layout')?.classList.add('fullscreen');

        } else {
          this.showTopbar = true;
          this.showMenubar = true;
          this.showFooter = true;

          document.querySelector('.isg-page-layout')?.classList.remove('fullscreen');

        }
      }
    });


    // Process MS Clarity Script if required

    try {

      // If the Clarity Key exists, then load the Clarity script
      const claritySettings = configSettings[<any>'clarity'];
      const clarityKey = claritySettings[<any>'key'];
      if (clarityKey && clarityKey.length > 0) {
        // Add script to the DOM
        let clarityScriptTag = document.createElement('script');
        clarityScriptTag.type = 'text/javascript';
        clarityScriptTag.async = true;
        //clarityScriptTag.innerHTML = this.sanitizer.bypassSecurityTrustScript(clarityScript(clarityKey)) as string;
        clarityScriptTag.innerHTML = clarityScript(clarityKey);
        document.head.appendChild(clarityScriptTag);
      }
    } catch (error) {
      console.log('Error loading Clarity script');
      console.log(error);
    }

  }

  toggleMenu() {
    this.menuHidden = !this.menuHidden;
    this.cdr.detectChanges();
  }

  ngOnInit() {
    // Handle a logout by stopping the idle timer
    this.wrapper.loggedOut.subscribe(() => {
      this._idle.stop();
    });

    this._idle.setIdle(this.idleLength);
    this._idle.setTimeout(this.idleTimeout);
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this._idle.onIdleStart.subscribe(() => {
      // clear inturupts to stop the user from being able to cancel the idle outside of the dialog
      this._idle.clearInterrupts();

      // show the modal
      const modal = this.modalService.open(this.idleContent, { centered: true });
      modal.result.then((result) => {
        // modal closed by logout          
        this.logout();
      }, (reason) => {
        //  // modal closed by cancel
        this._idle.stop();
        this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this._idle.watch();

      });
    });

    this._idle.onIdleEnd.subscribe(() => {
      // hide the modal

      // dont worry about hiding the user should hit close to continue.
      // this.modalService.dismissAll();
    });
    this._idle.onTimeoutWarning.subscribe((secondsLeft: number) => {
      // Update the warning message
      this.idleCountdown = secondsLeft;
    });
    this._idle.onTimeout.subscribe(() => {
      // Hide the modal, 
      this.modalService.dismissAll();
      this.logout();
    });


    //// If authenticated then start the idle timer
    //this.oktaAuth.isAuthenticated().then(auth => {
    //});

    this.wrapper.authenticationChanged.subscribe(auth => {
      if (auth) {
        this._idle.watch();
      }
      else {
        this._idle.stop();
      }
    });

  }

  private logout(): void {
    this.wrapper.logout();
    this.userService.clearLocalStorage();
  }

  public menuClick() {
    this.toggleMenu();
  }

  protected onImpersonateChanged(email: string) {
    // Set the plain string and emit change
    this.impersonationEmail = email;
    this.impersonateChanged.emit(email);

    // Call refresh on the view child instance (safe null-check)
    this.compMenuVertical?.refreshMenu();
  }

  ngOnDestroy(): void {
    // Clean up any subscriptions or resources here if needed
  }
}
